'use client';

import { forwardRef } from 'react';
import { createPolymorphicComponent, Highlight as MantineHighlight } from '@mantine/core';
import type { HighlightProps as MantineHighlightProps } from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

// `highlightStyles` is locked alongside LockedProps: it's an inline-style escape hatch for the
// `mark` elements, and the highlight fill is theme-owned (see the theme's `Highlight` entry, which
// sets `color` — it cannot live on `Mark`, for the reason documented there).
export type HighlightProps = Omit<MantineHighlightProps, LockedProps | 'highlightStyles'>;

/**
 * ### Highlight
 *
 * @description
 * Mantine's Highlight minus per-call-site style overrides (see {@link LockedProps}). A `Text` that
 * wraps matches of `highlight` in `mark` elements — regex special characters are escaped and
 * matching is case-insensitive by default. Style it with the same props as `Text`
 * (`size`, `c`, `fw`, …); the highlight colour comes from the theme's `Highlight` entry.
 *
 * @example
 * ```tsx
 * <Highlight highlight={searchText}>{title}</Highlight>
 * <Highlight size="sm" fw={600} highlight={searchText ?? ''}>{module.displayName}</Highlight>
 * ```
 *
 * @param props - {@link HighlightProps}
 * @returns React component
 */
const _Highlight = forwardRef<HTMLDivElement, HighlightProps>((props, ref) => (
  <MantineHighlight ref={ref} {...props} />
));
_Highlight.displayName = 'Highlight';

export const Highlight = createPolymorphicComponent<'div', HighlightProps>(
  _Highlight,
) as LockedPolymorphic<'div', HighlightProps>;
